/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Empty, Modal, Typography } from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
import EchartsWrapper from './EchartsWrapper';
import ChartLoader from './ChartLoader';

// Misc imports
import is from '../../utils/is';
import { formatNumberToThousandSeparator } from '../../utils/string/formatNumberToThousandSeparator';
import useTheme from '../../utils/hooks/useTheme';
import AnalyticsTable from '../Tables/AnalyticsTable';

const MissedQuestionsTotalSection = ({ data = {}, sectionTitle = '', seriesName = '', isLoading = true, renderOnlyChart = false, renderOnlyTable = false }) => {
  const [chartToFullscreen, setChartToFullscreen] = useState();
  const { t } = useTranslation();
  const { fontSizeLg, fontSizeXxl } = useTheme();

  const tableConfig = {
    descriptionTableData: {
      ...data,
      answeredQuestions: data.totalQuestions - data.totalMissedQuestions
    },
    isDescriptionTable: true,
    isTableLoading: isLoading,
    emptyLines: 0,
    descriptionTableFields: [
      {
        label: t('Missed Questions'),
        dataField: 'totalMissedQuestions',
        isPercentage: false,
        isToBeFixed: false
      },
      {
        label: t('Answered Questions'),
        dataField: 'answeredQuestions',
        isPercentage: false,
        isToBeFixed: false,
        isToBeConvertedToSecOrMinutes: false
      },
      {
        label: t('Total Questions'),
        dataField: 'totalQuestions',
        isPercentage: false,
        isToBeFixed: false,
        isToBeConvertedToSecOrMinutes: false
      }
    ]
  };
  const getOptions = (totalQuestions, totalMissedQuestions, missedQuestionsPercentage) => {
    return {
      color: ['#e2e1ff', '#605dec'],
      title: {
        text: t('Missed'),
        subtext: `${!isNaN(missedQuestionsPercentage) ? missedQuestionsPercentage.toFixed(1) : 0}%`,
        left: 'center',
        top: '165px',
        textStyle: {
          fontSize: fontSizeLg,
          lineHeight: 1.5,
          fontWeight: 'normal',
          color: 'black'
        },
        subtextStyle: {
          fontSize: fontSizeXxl,
          lineHeight: 1,
          color: 'black'
        }
      },
      legend: {
        type: 'scroll',
        show: true,
        data: [t('Answered Questions'), t('Missed Questions')],
        bottom: 0
      },
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          return `${t('Total Questions')}: ${formatNumberToThousandSeparator(totalQuestions)}<br />${params.marker}${params.name}: ${formatNumberToThousandSeparator(params.value)} (${params.percent ? params.percent : 0}%)`;
        },
        confine: true,
        triggerOn: 'mousemove|click',
        enterable: true,
        renderMode: 'html'
      },
      series: [
        {
          name: t('Answered Questions'),
          type: 'pie',
          startAngle: 40,
          radius: ['55%', '70%'],
          data: [
            { value: totalQuestions - totalMissedQuestions, name: t('Answered Questions') },
            { value: totalMissedQuestions, name: t('Missed Questions') }
          ],
          label: {
            show: true,
            position: 'outside',
            formatter: (params) => {
              return `${params.value}`;
            },
            labelLine: {
              show: false,
              smooth: true,
              lineStyle: {
                color: '#000000'
              }
            }
          },
          emphasis: {
            label: {
              show: true
            }
          }
        }
      ]
    };
  };

  const chart
    = (<Fragment>
      {
        data?.totalQuestions > 0
          ? <EchartsWrapper
            chartOptions={getOptions(data.totalQuestions, data.totalMissedQuestions, data.missedQuestionsPercentage)}
            style={{ height: 367 }}
            loading={isLoading}
          />
          : <div className="d-flex content-fill flex-column h-100">
            <div className="mt-5 d-flex align-items-center mb-auto text-center flex-auto align-self-center">
              <Empty
                description={(
                  <Typography.Paragraph>
                    {t('No data available for this chart')}
                  </Typography.Paragraph>
                )}
              />
            </div>
          </div>

      }
    </Fragment>
    );

  return (
    <Fragment>
      {
        renderOnlyChart ? chart
          : <Fragment>
            <div className="mb-6 position-relative">
              <Typography.Title level={4} className="mb-6">
                <Typography.Text className="mr-2">{sectionTitle}</Typography.Text>
              </Typography.Title>

              {isLoading ? <ChartLoader /> : chart}

              <Button
                type="text"
                size="large"
                disabled={isLoading}
                icon={<FullscreenOutlined />}
                className="position-absolute top-0 right-0"
                onClick={() => setChartToFullscreen(chart)}
              />
            </div>

            <AnalyticsTable
              descriptionTableData={tableConfig.descriptionTableData}
              isDescriptionTable={tableConfig.isDescriptionTable}
              isTableLoading={tableConfig.isTableLoading}
              emptyLines={tableConfig.emptyLines}
              descriptionTableFields={tableConfig.descriptionTableFields}
            />

            <Modal
              open={is.truthy(chartToFullscreen)}
              onCancel={() => setChartToFullscreen()}
              destroyOnClose
              footer={null}
              centered
              style={{ maxWidth: 1000 }}
              width={'95%'}
            >
              <div className="p-5">
                {chartToFullscreen}
              </div>
            </Modal>
          </Fragment>

      }
    </Fragment>
  );
};

MissedQuestionsTotalSection.propTypes = {
  data: PropTypes.object,
  sectionTitle: PropTypes.string,
  seriesName: PropTypes.string,
  isLoading: PropTypes.bool,
  renderOnlyChart: PropTypes.bool,
  renderOnlyTable: PropTypes.bool
};

export default MissedQuestionsTotalSection;
